import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiArrowDown, FiArrowUp, FiGrid, FiTrash2 } from 'react-icons/fi';
import { getCashRegisters, getCategories, getProducts } from '../api';
import { CLIENT_TYPE_OPTIONS, normalizeClientType, resolvePriceClientType } from '../utils/clientType';
import { normalizePaginatedResponse } from '../utils/pagination';
import {
  buildResolvedPosSlots,
  getPosSlotLayout,
  movePosSlot,
  removePosSlotAt,
  setPosSlotLayout,
} from '../utils/posSlotLayouts';
import CategoryFilterBar from './CategoryFilterBar';
import InlineLoading from './InlineLoading';

const normalizeItems = (payload) => normalizePaginatedResponse(payload).data || [];

const PosSlotSettingsPanel = ({
  branchId,
  branchLabel = 'Sucursal seleccionada',
  preferences,
  preferencesLoading = false,
  preferencesError = '',
  savePreferences,
  canAccessRegisters = true,
  canReadProducts = true,
  canReadCategories = true,
}) => {
  const [cashRegisters, setCashRegisters] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCashRegisterId, setSelectedCashRegisterId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [slotLoading, setSlotLoading] = useState(false);
  const [slotSaving, setSlotSaving] = useState(false);
  const [slotMessage, setSlotMessage] = useState('');
  const [slotError, setSlotError] = useState('');

  useEffect(() => {
    if (!branchId || !canAccessRegisters || !canReadProducts) {
      setCashRegisters([]);
      setProducts([]);
      setCategories([]);
      setSelectedCashRegisterId('');
      setSelectedCategory('all');
      return;
    }

    let ignore = false;
    const fetchContext = async () => {
      setSlotLoading(true);
      setSlotError('');
      try {
        const [cashRegisterResponse, productResponse, categoryResponse] = await Promise.all([
          getCashRegisters(branchId, { pageSize: 0 }),
          getProducts(branchId, { pageSize: 0 }),
          canReadCategories
            ? getCategories({ includeAvailability: true, branchId })
            : Promise.resolve([]),
        ]);
        if (ignore) return;
        const nextCashRegisters = normalizeItems(cashRegisterResponse);
        setCashRegisters(nextCashRegisters);
        setProducts(normalizeItems(productResponse));
        setCategories(canReadCategories ? normalizeItems(categoryResponse) : []);
        setSelectedCashRegisterId((current) => {
          if (current && nextCashRegisters.some((entry) => String(entry.id) === String(current))) {
            return current;
          }
          return nextCashRegisters[0]?.id ? String(nextCashRegisters[0].id) : '';
        });
      } catch (requestError) {
        if (ignore) return;
        console.log(requestError);
        setSlotError('Error al cargar cajas o productos para configurar los favs.');
        setCashRegisters([]);
        setProducts([]);
        setCategories([]);
        setSelectedCashRegisterId('');
      } finally {
        if (!ignore) setSlotLoading(false);
      }
    };

    fetchContext();
    return () => {
      ignore = true;
    };
  }, [branchId, canAccessRegisters, canReadCategories, canReadProducts]);

  useEffect(() => {
    setSlotMessage('');
    setSlotError('');
    setSelectedCategory('all');
  }, [selectedCashRegisterId]);

  const resolveProductCategoryId = useCallback((product) => {
    return product?.categoryId ?? product?.Category?.id ?? null;
  }, []);

  const activeCashRegister = useMemo(
    () => cashRegisters.find((entry) => String(entry.id) === String(selectedCashRegisterId)) || null,
    [cashRegisters, selectedCashRegisterId]
  );

  const registerAllowedClientTypes = useMemo(() => {
    const normalized = [];
    const seen = new Set();
    (activeCashRegister?.allowedClientTypes || []).forEach((entry) => {
      const value = normalizeClientType(entry);
      if (!value || seen.has(value)) return;
      normalized.push(value);
      seen.add(value);
    });
    return normalized;
  }, [activeCashRegister?.allowedClientTypes]);

  const registerAllowedClientTypeLabels = useMemo(() => {
    if (!registerAllowedClientTypes.length) return 'Todos los tipos';
    return CLIENT_TYPE_OPTIONS
      .filter((option) => registerAllowedClientTypes.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  }, [registerAllowedClientTypes]);

  const isPriceAllowedByRegister = useCallback((price) => {
    if (!price || price.price === null || price.price === undefined) return false;
    if (!registerAllowedClientTypes.length) return true;
    const priceType = normalizeClientType(resolvePriceClientType(price));
    return registerAllowedClientTypes.includes(priceType);
  }, [registerAllowedClientTypes]);

  const currentPosSlotLayout = useMemo(
    () => getPosSlotLayout(preferences?.posSlotLayouts, branchId, selectedCashRegisterId, products),
    [branchId, preferences?.posSlotLayouts, products, selectedCashRegisterId]
  );

  const resolvedPosSlots = useMemo(() => buildResolvedPosSlots({
    products,
    slotLayout: currentPosSlotLayout,
    quantities: {},
    discounts: {},
    isPriceAllowedByRegister,
    resolveDisplayPrice: (_variety, price) => ({
      price: Number(price?.price || 0),
      taxFreeApplied: false,
    }),
  }), [currentPosSlotLayout, isPriceAllowedByRegister, products]);

  const slotCategoryIds = useMemo(() => {
    const ids = new Set();
    resolvedPosSlots.forEach((slot) => {
      const categoryId = resolveProductCategoryId(slot.product);
      if (Number.isInteger(categoryId)) ids.add(categoryId);
    });
    return ids;
  }, [resolvedPosSlots, resolveProductCategoryId]);

  const slotCategories = useMemo(() => (
    (categories || [])
      .filter((category) => category?.isActive !== false)
      .filter((category) => slotCategoryIds.has(category.id))
  ), [categories, slotCategoryIds]);

  useEffect(() => {
    if (selectedCategory === 'all') return;
    if (slotCategories.some((category) => String(category.id) === String(selectedCategory))) return;
    setSelectedCategory('all');
  }, [selectedCategory, slotCategories]);

  const filteredResolvedPosSlots = useMemo(() => {
    if (selectedCategory === 'all') return resolvedPosSlots;
    return resolvedPosSlots.filter(
      (slot) => String(resolveProductCategoryId(slot.product)) === String(selectedCategory)
    );
  }, [resolvedPosSlots, resolveProductCategoryId, selectedCategory]);

  const persistCurrentSlotLayout = useCallback(async (nextLayout, successText) => {
    if (!branchId || !selectedCashRegisterId || typeof savePreferences !== 'function') return;
    setSlotSaving(true);
    setSlotMessage('');
    setSlotError('');
    try {
      const nextLayouts = setPosSlotLayout(
        preferences?.posSlotLayouts,
        branchId,
        selectedCashRegisterId,
        nextLayout,
        products
      );
      await savePreferences({ posSlotLayouts: nextLayouts });
      setSlotMessage(successText);
    } catch (requestError) {
      console.log(requestError);
      setSlotError(requestError?.response?.data?.message || requestError?.message || 'No se pudieron guardar los favs.');
    } finally {
      setSlotSaving(false);
    }
  }, [branchId, preferences?.posSlotLayouts, products, savePreferences, selectedCashRegisterId]);

  const handleMoveSlot = useCallback((fromIndex, toIndex) => {
    const nextLayout = movePosSlot(currentPosSlotLayout, fromIndex, toIndex);
    persistCurrentSlotLayout(nextLayout, 'Orden de favs actualizado.');
  }, [currentPosSlotLayout, persistCurrentSlotLayout]);

  const handleRemoveSlot = useCallback((slotIndex) => {
    const nextLayout = removePosSlotAt(currentPosSlotLayout, slotIndex);
    persistCurrentSlotLayout(nextLayout, 'Fav eliminado de la configuracion.');
  }, [currentPosSlotLayout, persistCurrentSlotLayout]);

  const slotStatus = slotError || preferencesError;
  const slotsDisabled = slotLoading || preferencesLoading || slotSaving || !selectedCashRegisterId;

  if (!branchId) {
    return (
      <div className="border rounded-lg p-4 text-sm text-gray-600">
        Selecciona una sucursal para administrar los favs del POS.
      </div>
    );
  }

  if (!canAccessRegisters || !canReadProducts) {
    return (
      <div className="border rounded-lg p-4 text-sm text-gray-600">
        No tienes permisos para ver cajas y productos.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-start gap-2 text-gray-700">
          <FiGrid className="mt-0.5" />
          <div>
            <h2 className="text-md font-semibold">Favs POS</h2>
            <p className="text-xs text-gray-500">
              Ordena o elimina los favs guardados para tu usuario en cada caja. Los nuevos favs se agregan desde la vista Favs del POS.
            </p>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,240px)_minmax(0,1fr)]">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sucursal</label>
              <input
                type="text"
                value={branchLabel || branchId}
                disabled
                className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50 text-gray-700"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Caja</label>
              <select
                value={selectedCashRegisterId}
                onChange={(e) => setSelectedCashRegisterId(e.target.value)}
                disabled={slotLoading || !cashRegisters.length}
                className="w-full border border-gray-300 rounded-lg p-3 bg-white text-gray-700 disabled:bg-gray-50 disabled:text-gray-400"
              >
                {!cashRegisters.length && <option value="">Sin cajas disponibles</option>}
                {cashRegisters.map((register) => (
                  <option key={register.id} value={register.id}>
                    {register.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              <div className="font-semibold text-gray-800">Tipos permitidos</div>
              <div className="mt-1">{registerAllowedClientTypeLabels}</div>
            </div>
          </div>

          <div className="space-y-3">
            {slotLoading && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                <InlineLoading label="Cargando cajas y productos" />
              </div>
            )}
            {slotMessage && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {slotMessage}
              </div>
            )}
            {slotStatus && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {slotStatus}
              </div>
            )}
            {!slotLoading && !cashRegisters.length && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                No hay cajas disponibles en la sucursal actual.
              </div>
            )}
            {!slotLoading && selectedCashRegisterId && slotCategories.length > 0 && (
              <CategoryFilterBar
                categories={slotCategories}
                selectedCategory={selectedCategory}
                onSelect={setSelectedCategory}
                includeAll
                allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
                emptyMessage="No hay categorias con favs configurados."
                isEmpty={slotCategories.length === 0}
              />
            )}
            {!slotLoading && selectedCashRegisterId && resolvedPosSlots.length === 0 && (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center text-sm text-gray-500">
                No tienes favs guardados para esta caja.
              </div>
            )}
            {!slotLoading && selectedCashRegisterId && resolvedPosSlots.length > 0 && filteredResolvedPosSlots.length === 0 && (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center text-sm text-gray-500">
                No hay favs en esta categoria.
              </div>
            )}
            {!slotLoading && filteredResolvedPosSlots.map((slot) => (
              <div
                key={slot.key}
                className={`rounded-xl border px-4 py-4 ${
                  slot.disabled
                    ? 'border-amber-200 bg-amber-50/60'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      Slot {slot.slotIndex + 1}
                    </div>
                    <div className="mt-1 text-base font-semibold text-gray-900">
                      {slot.productName}
                    </div>
                    {slot.detailLabel ? (
                      <div className="mt-1 text-sm text-gray-600">{slot.detailLabel}</div>
                    ) : null}
                    <div className="mt-2 text-sm text-gray-500">
                      {slot.priceLabel}
                    </div>
                    {slot.invalidReason ? (
                      <div className="mt-2 text-xs text-amber-700">
                        {slot.invalidReason}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleMoveSlot(slot.slotIndex, slot.slotIndex - 1)}
                      disabled={slotsDisabled || slot.slotIndex === 0}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FiArrowUp />
                      Subir
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveSlot(slot.slotIndex, slot.slotIndex + 1)}
                      disabled={slotsDisabled || slot.slotIndex === currentPosSlotLayout.length - 1}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FiArrowDown />
                      Bajar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveSlot(slot.slotIndex)}
                      disabled={slotsDisabled}
                      className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FiTrash2 />
                      Quitar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosSlotSettingsPanel;
